import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { translateText } from '../../../helpers/LanguageHelper';
import { buildOrderUpiLink, PAYMENT_WHATSAPP_NUMBER } from '../../../helpers/OrderQrPaymentHelper';
import './OrderQrPayment.css';

const OrderQrPayment = ({ orderNumber, amount, upiId, payeeName, ruleId }) => {
    const [qrImage, setQrImage] = useState(null);
    const [qrError, setQrError] = useState(false);
    const [hasPaid, setHasPaid] = useState(false);
    const [screenshot, setScreenshot] = useState(null);
    const [preview, setPreview] = useState('');
    const [message, setMessage] = useState('');
    const upiLink = buildOrderUpiLink(amount, orderNumber, upiId, payeeName);
    const currentQrImage = qrImage?.link === upiLink ? qrImage.image : '';
    const paymentMessage = translateText('Payment screenshot for UKL Butter order #{number}, amount INR {amount}. Please verify my payment.').replace('{number}', () => orderNumber).replace('{amount}', () => Number(amount).toFixed(2));
    const whatsappLink = `https://wa.me/${PAYMENT_WHATSAPP_NUMBER}?text=${encodeURIComponent(paymentMessage)}`;
    const canShareFile = Boolean(screenshot && navigator.canShare && navigator.canShare({ files: [screenshot] }));

    useEffect(() => {
        let active = true;
        setQrImage(null);
        setQrError(false);
        if (!upiLink) { setQrError(true); return () => { active = false; }; }
        QRCode.toDataURL(upiLink, { width: 280, margin: 4, errorCorrectionLevel: 'M' })
            .then(image => { if (active) setQrImage({ link: upiLink, image }); })
            .catch(() => { if (active) setQrError(true); });
        return () => { active = false; };
    }, [upiLink]);

    useEffect(() => {
        if (!screenshot) { setPreview(''); return; }
        const url = URL.createObjectURL(screenshot);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [screenshot]);

    const selectScreenshot = event => {
        const file = event.target.files?.[0];
        setMessage('');
        setScreenshot(null);
        if (!file) return;
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size > 10 * 1024 * 1024) {
            setMessage('Please choose a JPG, PNG or WebP image smaller than 10 MB.');
            event.target.value = '';
            return;
        }
        setScreenshot(file);
    };

    const shareScreenshot = async () => {
        try {
            await navigator.share({ files: [screenshot], title: `${translateText('Order Number')} #${orderNumber}`, text: paymentMessage });
            setMessage('If you sent the screenshot to 6380413953, our team will verify your payment.');
        } catch (error) {
            if (error.name !== 'AbortError') setMessage('Sharing is unavailable. Open WhatsApp and attach your screenshot there.');
        }
    };

    if (!upiLink) return <p role="alert">{translateText('Payment details are unavailable. Please refresh or contact us on WhatsApp.')}</p>;

    return (
        <section className="ukl-order-qr" aria-labelledby={`order-qr-title-${ruleId || 0}`}>
            <span className="ukl-order-qr__label">{translateText('UPI payment')}</span>
            <h2 id={`order-qr-title-${ruleId || 0}`}>{translateText('Pay and share a screenshot')}</h2>
            <strong className="ukl-order-qr__amount">₹{amount.toFixed(2)}</strong>
            <p>{translateText('Scan with any UPI app.')}</p>
            <div className="ukl-order-qr__frame">
                <span className="ukl-order-qr__brand">{payeeName}</span>
                <div className="ukl-order-qr__surface">
                    {currentQrImage && <img className="ukl-order-qr__code" src={currentQrImage} width="280" height="280" alt={translateText('Payment QR for order {number}').replace('{number}', () => orderNumber)} />}
                    {!currentQrImage && <p role="status">{translateText(qrError ? 'QR could not load. Use the UPI app link below or contact us on WhatsApp.' : 'Preparing your payment QR...')}</p>}
                </div>
                <span className="ukl-order-qr__reference">{translateText('Order Number')}: {orderNumber}</span>
            </div>
            <a className="ukl-order-qr__button" href={upiLink}>{translateText('Open UPI app')}</a>
            <div className="ukl-order-qr__paid-reminder" role="note">
                <span className="ukl-order-qr__paid-icon" aria-hidden="true"><i className="fa fa-exclamation-circle" /></span>
                <div>
                    <strong>{translateText('Already paid? Do not pay again.')}</strong>
                    <span>{translateText('Share your screenshot below.')}</span>
                    <span>{translateText('After payment, please get confirmation from us that your payment was received successfully.')}</span>
                </div>
            </div>
            {!hasPaid ? (
                <button type="button" className="ukl-order-qr__button ukl-order-qr__button--secondary" onClick={() => setHasPaid(true)}>{translateText('I have paid — share screenshot')}</button>
            ) : (
                <div className="ukl-order-qr__proof">
                    <h3>{translateText('Share your payment screenshot')}</h3>
                    <p>{translateText('Send your screenshot to WhatsApp 6380413953 with your order number. Payment is confirmed after our team verifies it.')}</p>
                    <label htmlFor={`order-payment-screenshot-${ruleId || 0}`}>{translateText('Choose screenshot (JPG, PNG or WebP, up to 10 MB)')}</label>
                    <input id={`order-payment-screenshot-${ruleId || 0}`} type="file" accept="image/jpeg,image/png,image/webp" onChange={selectScreenshot} />
                    {preview && <img className="ukl-order-qr__preview" src={preview} alt={translateText('Selected payment screenshot')} />}
                    {canShareFile && <button type="button" className="ukl-order-qr__button" onClick={shareScreenshot}>{translateText('Share screenshot — choose WhatsApp')}</button>}
                    <a className="ukl-order-qr__button ukl-order-qr__button--secondary" href={whatsappLink} target="_blank" rel="noopener noreferrer">{translateText('Open WhatsApp to send screenshot')}</a>
                    <p className="ukl-order-qr__hint">{translateText('On a computer, open WhatsApp, click Attach and choose your screenshot. Selecting a file here only previews it; it is not uploaded or sent automatically.')}</p>
                    {message && <p role="status">{translateText(message)}</p>}
                </div>
            )}
        </section>
    );
};

export default OrderQrPayment;
